import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { Input, TextArea } from "../../../../../common/Input";
import { StatusBadge } from "../../../../../common/Badge";
import { ApprovalTrail } from "./ApprovalTrail";

export function RequestDetailsModal({ isOpen, onClose, request }) {
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (isOpen) setActiveTab("details");
  }, [isOpen]);

  if (!isOpen || !request) return null;

  const renderTypeSpecificFields = () => {
    // ... [Same logic as before for OT/Leave/Offset dates/hours] ...
  };

  const tabs = [
    { key: "details", label: "Request Info", icon: <DescriptionIcon fontSize="small" /> },
    { key: "trail", label: "Approval Trail", icon: <AssignmentTurnedInIcon fontSize="small" /> },
  ];

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto text-gray-700">
      <div className="bg-white w-full md:w-[80%] lg:w-[60%] xl:w-[50%] rounded-lg shadow-2xl flex flex-col h-[85vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2 uppercase">
            <AccountBalanceWalletIcon className="text-blue-700" />
            {request.requestTypeDescription}: {request.formattedReferenceNumber}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer">
            <CloseIcon />
          </button>
        </div>

        {/* Tab Navigation (Salary Advance Style) */}
        <div className="border-b border-gray-200 bg-white pt-3">
          <div className="flex space-x-1 px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 text-sm font-bold rounded-t-lg transition-colors border-b-2 flex items-center gap-2 border-none cursor-pointer whitespace-nowrap ${activeTab === tab.key
                    ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto flex-grow bg-white">
          {activeTab === "details" ? (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg border shadow-sm flex flex-col gap-1 ${request.status === "Approved" ? "bg-green-50 border-green-200 text-green-800" :
                  request.status === "Disapproved" ? "bg-red-50 border-red-200 text-red-800" :
                    "bg-amber-50 border-amber-200 text-amber-800"
                }`}>
                <div className="flex justify-between items-center">
                  <span className="font-bold uppercase tracking-wider text-xs">Current Overall Status</span>
                  <StatusBadge status={request.status} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <Input label="Ref No." value={request.formattedReferenceNumber} isViewOnly />
                <Input label="Employee" value={request.fullName} isViewOnly />
                <Input label="Request Date" value={new Date(request.requestDate).toLocaleDateString()} isViewOnly />
                {renderTypeSpecificFields()}
                <div className="md:col-span-2">
                  <TextArea label="Employee Remarks / Reason" value={request.reason} isViewOnly rows={4} />
                </div>
                {request.status === "Disapproved" && (
                  <div className="md:col-span-2 p-3 bg-red-50 rounded border border-red-100 italic text-sm">
                    <strong>Rejection Reason:</strong> {request.reasonForRejection || "No specific reason provided."}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <div className="h-[1px] bg-gray-200 flex-grow"></div>
                Approval Sequence
                <div className="h-[1px] bg-gray-200 flex-grow"></div>
              </h4>
              <ApprovalTrail trail={request.approvalTrail} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
          <span className="text-[10px] text-gray-400 font-bold uppercase px-2">ID: {request.id}</span>
          <button onClick={onClose} className="px-10 py-2 bg-gray-400 text-white rounded font-bold cursor-pointer border-none text-sm hover:bg-gray-500">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}